<template>
    <div class="">
        <easy-data-table
            class="table"
            :headers="headers"
            :items="$page.props.students"
            :hide-footer="hideFooter"
            :rows-per-page="10"
            :rows-items="[10, 25, 50]"
            buttons-pagination
            must-sort
            :empty-message="emptyMessage"
        >
            <template #item-full_name="student">
                <Link
                    class="group hover:cursor-pointer hover:text-blue-500"
                    :href="
                        route('group.student.show', {
                            user: student.id,
                        })
                    "
                >
                    <div class="flex items-center gap-2 whitespace-nowrap">
                        <UserAvatarRound :user="student" size="28" />
                        <span class="group-hover:underline">
                            <span
                                v-if="
                                    student.subscription.status !== 'incomplete'
                                "
                            >
                                {{ student.full_name }}</span
                            >
                            <span v-else> {{ student.email }}</span>
                        </span>
                    </div>
                </Link>
            </template>

            <template #item-provider_level.id="student">
                <ProviderLevelLabel
                    :providerLevelTitle="student.provider_level.title"
                />
            </template>

            <template #item-time_spent_studying="student">
                {{ student.time_spent_studying_readable }}
            </template>

            <template #item-scores="student">
                <div class="flex gap-2" v-if="student.performance">
                    <template
                        v-for="focusArea in student.performance
                            .focus_area_scores"
                    >
                        <Tooltip
                            :width-px="320"
                            css-classes="border-gray-200 bg-white p-4 drop-shadow-md md:w-[300px] xl:w-[420px]"
                        >
                            <template #trigger>
                                <focus-area-score-round-label
                                    :score="focusArea.user_average"
                                />
                            </template>
                            <template #content>
                                <div class="">
                                    <div
                                        class="mb-4 border-b border-gray-400 pb-1 font-semibold"
                                    >
                                        <span
                                            class="border-b-3 border-blue-300 pb-1"
                                        >
                                            {{ focusArea.title }}
                                        </span>
                                    </div>

                                    <div
                                        class="flex flex-col gap-2 xl:flex-row xl:gap-4"
                                    >
                                        <div class="">
                                            <div
                                                class="h-4 font-[13px] font-semibold text-gray-650"
                                            >
                                                <span> </span>
                                                <Tooltip :width-px="240">
                                                    <template #trigger>
                                                        <span
                                                            class="underline decoration-dotted hover:text-blue-300"
                                                        >
                                                            Recent
                                                        </span>
                                                    </template>
                                                    <template #content>
                                                        <div class="">
                                                            <div
                                                                class="mb-2 font-semibold"
                                                            >
                                                                In the last 5
                                                                Active Days
                                                            </div>
                                                            <div
                                                                class="text-[13px] text-gray-450"
                                                            >
                                                                An “active day”
                                                                is one in which
                                                                a student has
                                                                answered at
                                                                least 1 question
                                                                in this
                                                                category.
                                                            </div>
                                                        </div>
                                                    </template>
                                                </Tooltip>
                                            </div>
                                            <div class="mt-1">
                                                <span class="font-semibold">
                                                    {{
                                                        focusArea.recent_questions_correct
                                                    }}/{{
                                                        focusArea.recent_questions_answered
                                                    }}
                                                </span>
                                                correct
                                            </div>
                                        </div>
                                        <div class="">
                                            <div
                                                class="h-4 font-[13px] font-semibold text-gray-650"
                                            >
                                                All Time
                                            </div>
                                            <div class="mt-1">
                                                <span class="font-semibold">
                                                    {{
                                                        focusArea.total_questions_correct
                                                    }}/{{
                                                        focusArea.total_questions_answered
                                                    }}
                                                </span>
                                                correct
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Tooltip>
                    </template>
                </div>
                <div v-else class="flex gap-2">
                    <div
                        v-for="index in 5"
                        :key="index"
                        class="flex h-10 w-12 items-center justify-center whitespace-nowrap rounded rounded-full bg-gray-100 px-4 font-medium"
                    >
                        --%
                    </div>
                </div>
            </template>

            <template #item-classroom_goals="student">
                <div class="flex items-center justify-start gap-2">
                    <template v-for="(goal, index) in classroom.goals">
                        <div
                            v-if="goal.enabled"
                            :class="
                                'text-medium flex h-10 w-10 items-center justify-center rounded rounded-full text-[15px]' +
                                ' ' +
                                (student.classroom_goals[index]
                                    ? 'bg-blue-300 text-white'
                                    : 'bg-gray-250 text-gray-850')
                            "
                        >
                            {{ goal.label }}
                        </div>
                    </template>
                </div>
            </template>

            <template #item-actions="student">
                <dropdown open-from="right" :px-width="260">
                    <template #button>
                        <more-dots-button />
                    </template>
                    <template #content>
                        <ul class="rounded border">
                            <li>
                                <Link
                                    class="link block p-4"
                                    :href="
                                        route('group.student.show', {
                                            user: student.id,
                                        })
                                    "
                                    >Edit Student
                                </Link>
                            </li>
                        </ul>
                    </template>
                </dropdown>
            </template>
        </easy-data-table>
    </div>
</template>
<script>
import Dropdown from "@/Components/Dropdown";
import MoreDotsButton from "@/Components/MoreDotsButton";
import ProviderLevelLabel from "@/Components/ProviderLevelLabel";
import FormSearch from "@/Components/FormSearch";
import FocusAreaScoreRoundLabel from "@/Components/FocusAreaScoreRoundLabel";
import Tooltip from "@/Components/Tooltip";
import UserAvatarRound from "@/Components/UserAvatarRound";

export default {
    name: "GroupClassroomStudentsArchivedOverview",
    components: {
        UserAvatarRound,
        Tooltip,
        FocusAreaScoreRoundLabel,
        ProviderLevelLabel,
        Dropdown,
        MoreDotsButton,
        FormSearch,
    },
    props: {
        classroom: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            hideFooter: !(
                this.$page.props.students &&
                this.$page.props.students.length > 10
            ),
            headers: [
                {
                    text: "Student",
                    value: "full_name",
                    sortable: true,
                },
                {
                    text: "Level",
                    value: "provider_level.id",
                    sortable: true,
                },
                {
                    text: "Study Time",
                    value: "time_spent_studying",
                    sortable: true,
                },
                {
                    text: "Scores",
                    value: "scores",
                    sortable: true,
                },
                {
                    text: "Goals",
                    value: "classroom_goals",
                    width: 100,
                },
                {
                    text: "",
                    value: "actions",
                    width: 30,
                },
            ],
            emptyMessage: "0 students found",
        };
    },
    methods: {},
};
</script>
